import { useState } from "react";

/**
 * Adjust-balance form — Phase 11.
 *
 * Manual loyalty correction. Server enforces:
 *   • daily ±point cap per tenant_customer (DAILY_LIMIT_EXCEEDED).
 *   • non-negative resulting balance (INSUFFICIENT_BALANCE).
 *   • the chosen `currency` matches the tenant's loyalty mode
 *     (CURRENCY_MODE_MISMATCH).
 *
 * Delta is signed: positive = credit, negative = debit. Reason ≥5 chars.
 */
export function AdjustBalanceForm(props: {
  loyaltyMode: "POINTS" | "STAMPS" | "VISIT_TIERS" | "HYBRID";
  pointsBalance: number;
  stampsCount: number;
  onSubmit: (
    delta: number,
    currency: "POINTS" | "STAMPS",
    reason: string,
  ) => Promise<void>;
  busy: boolean;
}) {
  // For HYBRID tenants the cashier picks the currency; for single-mode
  // tenants we fix it to the obvious choice.
  const initialCurrency: "POINTS" | "STAMPS" =
    props.loyaltyMode === "STAMPS" ? "STAMPS" : "POINTS";

  const [currency, setCurrency] = useState<"POINTS" | "STAMPS">(initialCurrency);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (props.loyaltyMode === "VISIT_TIERS") {
    return (
      <p className="text-xs text-ink-dark">
        Manual adjustments aren't available for visit-tier loyalty.
      </p>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const n = Number(delta);
    if (!Number.isInteger(n) || n === 0) {
      setErr("Delta must be a non-zero integer.");
      return;
    }
    if (reason.trim().length < 5) {
      setErr("Reason must be at least 5 characters.");
      return;
    }
    try {
      await props.onSubmit(n, currency, reason.trim());
      setDelta("");
      setReason("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Adjustment failed.");
    }
  }

  const showCurrencyPicker = props.loyaltyMode === "HYBRID";

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3 text-xs text-ink-dark">
        <div>
          <span className="block uppercase tracking-wider">Points</span>
          <span className="font-mono text-navy">{props.pointsBalance.toFixed(2)}</span>
        </div>
        <div>
          <span className="block uppercase tracking-wider">Stamps</span>
          <span className="font-mono text-navy">{props.stampsCount}</span>
        </div>
      </div>

      {showCurrencyPicker && (
        <fieldset className="grid grid-cols-2 gap-2">
          <label
            className={`rounded-card border px-3 py-2 text-center text-sm cursor-pointer ${
              currency === "POINTS"
                ? "border-navy bg-navy text-white"
                : "border-stone-200"
            }`}
          >
            <input
              type="radio"
              name="currency"
              value="POINTS"
              checked={currency === "POINTS"}
              onChange={() => setCurrency("POINTS")}
              className="sr-only"
            />
            Points
          </label>
          <label
            className={`rounded-card border px-3 py-2 text-center text-sm cursor-pointer ${
              currency === "STAMPS"
                ? "border-navy bg-navy text-white"
                : "border-stone-200"
            }`}
          >
            <input
              type="radio"
              name="currency"
              value="STAMPS"
              checked={currency === "STAMPS"}
              onChange={() => setCurrency("STAMPS")}
              className="sr-only"
            />
            Stamps
          </label>
        </fieldset>
      )}

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
          Delta (positive = credit, negative = debit)
        </span>
        <input
          type="number"
          inputMode="decimal"
          step={currency === "POINTS" ? "0.01" : "1"}
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          required
          placeholder={currency === "POINTS" ? "e.g. 1.85 or -25.50" : "e.g. 50 or -25"}
          className="mt-1 input"
        />
      </label>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
          Reason
        </span>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          minLength={5}
          maxLength={200}
          placeholder="e.g. Goodwill credit for waiting time"
          className="mt-1 input"
        />
      </label>

      {err && (
        <div className="rounded-card border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {err}
        </div>
      )}

      <button
        type="submit"
        disabled={props.busy || delta.trim() === "" || reason.trim().length < 5}
        className="btn-primary w-full"
      >
        {props.busy ? "Adjusting…" : "Apply adjustment"}
      </button>
    </form>
  );
}
